import { useState, useEffect, useMemo, useRef } from 'react'
import { Wrapper, Status } from '@googlemaps/react-wrapper'
import { Marker } from '../components'
import { MapBase } from './MapBase'
import { DEFAULT_RIC_LOCATION, GOOGLE_MAPS_API_KEY } from '../common/constants'
import { useAccountContext } from '../contexts'
import { DeviceInfo } from '../common/enums'
import { SubLayout } from '../layouts/SubLayout'
import { useDeviceStatuses } from '../hooks/useDeviceStatuses'
import { getMarkerColor } from '../utils/deviceStatus'

// https://developers.google.com/maps/documentation/javascript/react-map

const INITIAL_ZOOM = 14

interface Props {
  focus: any
  selectDevice: (sigfoxId: string) => void
}

export function Map({ focus, selectDevice }: Props) {
  const { selectedDevice, devices } = useAccountContext()

  const [map, setMap] = useState<google.maps.Map>() // initial zoom
  const [zoom, setZoom] = useState(INITIAL_ZOOM) // initial zoom
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: focus.lat,
    lng: focus.lng,
  })

  // Fetch device statuses for marker colors
  // useMemo prevents creating a new array reference on every render
  const devicesArray = useMemo(() => Object.values(devices), [devices])
  const { deviceStatuses } = useDeviceStatuses(devicesArray)

  // Google Maps' built-in panTo() has a fixed animation speed that can't be
  // configured. To pan more slowly we run our own eased animation, interpolating
  // the map center from its current position to the target over PAN_DURATION_MS.
  // Bump/lower this single constant to taste (~2.5x slower than the native glide).
  const panAnimationRef = useRef<number>()
  useEffect(() => {
    if (!map) return

    const start = map.getCenter()
    if (!start) {
      map.setCenter(focus)
      return
    }

    const startLat = start.lat()
    const startLng = start.lng()
    const deltaLat = focus.lat - startLat
    const deltaLng = focus.lng - startLng

    // Nothing to animate (e.g. re-selecting the same device).
    if (Math.abs(deltaLat) < 1e-9 && Math.abs(deltaLng) < 1e-9) return

    const PAN_DURATION_MS = 1000 // native panTo glides in ~400ms; this is ~2.5x slower
    const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

    if (panAnimationRef.current) cancelAnimationFrame(panAnimationRef.current)

    let startTime: number | null = null
    const step = (now: number) => {
      if (startTime === null) startTime = now
      const t = Math.min((now - startTime) / PAN_DURATION_MS, 1)
      const eased = easeInOutCubic(t)
      map.setCenter({ lat: startLat + deltaLat * eased, lng: startLng + deltaLng * eased })
      if (t < 1) panAnimationRef.current = requestAnimationFrame(step)
    }
    panAnimationRef.current = requestAnimationFrame(step)

    return () => {
      if (panAnimationRef.current) cancelAnimationFrame(panAnimationRef.current)
    }
  }, [map, focus])

  function getPosition(device: DeviceInfo): google.maps.LatLngLiteral {
    const lat = device.coordinateNSValue || DEFAULT_RIC_LOCATION.lat
    const lng = device.coordinateEWValue || DEFAULT_RIC_LOCATION.lng
    return { lat, lng }
  }

  function onClick(e: google.maps.MapMouseEvent, map: google.maps.Map) {
    // avoid directly mutating state
    // setMarkers([...markers, e.latLng!])
    // map.panTo(e.latLng!)
  }

  function onIdle(m: google.maps.Map) {
    setMap(m)
    setZoom(m.getZoom()!)
    setCenter(m.getCenter()!.toJSON())
  }

  function render(status: Status) {
    return <h1>{status}</h1>
  }

  return (
    <SubLayout>
      <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} libraries={['marker']}>
        <MapBase
          center={center}
          onClick={onClick}
          onIdle={onIdle}
          zoom={zoom}
          fullscreenControl={false}
          streetViewControl={false}
          mapTypeControlOptions={{
            // style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
            mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
          }}
          style={{ flexGrow: '1', height: '100%' }}
        >
          {Object.values(devices)
            // .filter((d) => Object.keys(d.readings).length > 0)
            // .filter(onlyUnique)
            .map((device: DeviceInfo, index: number) => {
              // Get device status and corresponding marker color
              const statusData = deviceStatuses[device.sigfoxId]
              const status = statusData?.status || 'pending'
              const markerColor = getMarkerColor(status)

              return (
                <Marker
                  key={index}
                  sigfoxId={device.sigfoxId}
                  position={getPosition(device)}
                  index={index}
                  selectDevice={selectDevice}
                  selected={device.sigfoxId === selectedDevice}
                  markerColor={markerColor}
                />
              )
            })}
        </MapBase>
      </Wrapper>
    </SubLayout>
  )
}
